import React from 'react';
import styled from 'styled-components';
import BaseShow, { styles as baseStyles } from '../../core/Plans/Show';
import FavoritePlan from './Favorite/FavoritePlan';
import T from 'i18n-react';
import Skeleton from '@material-ui/lab/Skeleton';
import Typography from '@material-ui/core/Typography';
import BusinessInfo from '../Businesses/Info';
import { ANA, KYOTANGO } from '../../core/variables';
import { AnaAlert } from '../Features/Business/ana';
import clsx from 'clsx';
import ReviewContainer from './ReviewContainer';
import Application from './Application';
import AppLayout from '../Layout/AppLayout';
import { createPlanPageShareText } from '../../core/Social/TwitterButton';
import ShareButtonGroup from '../../core/Social/ShareButtonGroup';
import Hidden from '@material-ui/core/Hidden';
import { LOGIN_PROMPT_KEY } from '../../core/sessionStorages';
import AuthDialogContextProvider, {
  AuthDialogContext,
} from '../components/AuthDialog/AuthDialogContextProvider';
import AtmosphereSliders from '../../core/Plans/components/AtmosphereSliders';

const Container = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(5)}px;
`;

export const styles = (theme) => ({
  ...baseStyles,
  favoriteSkeleton: {
    maxWidth: 200,
    marginLeft: 'auto',
    borderRadius: theme.shape.borderRadius,
  },
  alert: {
    padding: theme.spacing(2, 0, 5),
  },
  alertRoot: {
    width: '100%',
    maxWidth: theme.breakpoints.values.md,
    lineHeight: 1.8,
    margin: 'auto',
    [theme.breakpoints.up('sm')]: {
      fontSize: theme.typography.pxToRem(18),
    },
  },
  subtitle: {
    ...baseStyles.subtitle,
    margin: theme.spacing(8, 'auto', 1),
  },
});

export class Show extends BaseShow {
  static contextType = AuthDialogContext;

  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
    };
  }
  getPlanId() {
    const found = window.location.pathname.match(/plans\/(\d+)/);
    return found?.[1];
  }

  getPlanProps = () => this.props;

  componentDidMount() {
    super.componentDidMount();
    const { authenticated } = this.props;

    const planId = this.getPlanId();
    if (!planId) return;

    const planData = window.history?.state?.plan;
    const { handleOpen: handleOpenAuthDialog } = this.context;

    if (planData) {
      this.setState({ ...planData, loaded: true });
    } else {
      const plan = this.getPlanProps();
      window.history?.replaceState?.({ plan, turbolinks: true }, null);
      this.setState({ ...plan, loaded: true });
    }

    if (!sessionStorage.getItem(LOGIN_PROMPT_KEY)) {
      // 画面が表示されて1秒後にダイアログを表示する
      setTimeout(() => {
        // ログインしていようがしていまいが、Session Storageに値を入れる
        sessionStorage.setItem(LOGIN_PROMPT_KEY, 'true');
        // ログインしていない場合のみダイアログを表示する
        !authenticated && handleOpenAuthDialog({ isSignup: true });
      }, 1000);
    }
  }

  getSnsText = () => {
    const { title, business } = this.state;
    const currentUrl = window.location.href.split(/[?#]/)[0];
    const regionName = business?.address?.region || '';
    const twitterShareText = createPlanPageShareText(
      title,
      currentUrl,
      regionName,
    );
    return { twitterText: twitterShareText, url: currentUrl };
  };

  slickRenderer(): * {
    const { classes } = this.props;
    const { loaded } = this.state;
    const { twitterText, url } = this.getSnsText();
    return (
      <>
        {super.slickRenderer()}
        <Hidden smUp>
          <div className={classes.container}>
            <ShareButtonGroup
              justify="center"
              loaded={loaded}
              tweetText={twitterText}
              url={url}
            />
          </div>
        </Hidden>
      </>
    );
  }

  favoriteRenderer() {
    const { classes } = this.props;
    const { loaded, title, favoriteCount, favoritePlan, authenticated } =
      this.state;
    const { twitterText, url } = this.getSnsText();

    return (
      <>
        <Hidden only="xs">
          <ShareButtonGroup
            loaded={loaded}
            gutterBottom
            tweetText={twitterText}
            url={url}
          />
        </Hidden>
        {loaded ? (
          <FavoritePlan
            buttonType="square"
            id="wannaGo"
            isFavorite={favoritePlan}
            isAuthenticated={authenticated}
            favoriteCount={favoriteCount}
            planId={this.getPlanId()}
            planName={title}
          />
        ) : (
          <div style={{ marginBottom: 32 }}>
            <Skeleton
              variant="rect"
              width="100%"
              height={40}
              className={classes.favoriteSkeleton}
            />
          </div>
        )}
      </>
    );
  }

  atmosphereSliderRenderer() {
    const { atmosphereSliders, classes } = this.props;

    return (
      !!atmosphereSliders?.length && (
        <>
          <Typography className={classes.subtitle}>
            {T.translate('plan.atmosphereSlider')}
          </Typography>
          <Container>
            <AtmosphereSliders atmosphereSliders={atmosphereSliders} />
          </Container>
        </>
      )
    );
  }

  businessInfoRenderer() {
    const {
      loaded,
      authenticated,
      followerCount,
      followingBusiness,
      tipMessage,
      tipAlreadyShown,
      business: {
        id,
        name,
        displayName,
        logo,
        homepage,
        url,
        address = {},
      } = {},
    } = this.state;

    return (
      <BusinessInfo
        loaded={loaded}
        id={id}
        name={name}
        displayName={displayName}
        homepage={homepage}
        logo={logo}
        url={url}
        address={address}
        authenticated={authenticated}
        followerCount={followerCount}
        isFollowing={followingBusiness}
        tipMessage={tipMessage}
        tipAlreadyShown={tipAlreadyShown}
        canShowShareButton={false}
      />
    );
  }

  otherRenderer() {
    const { classes } = this.props;
    const {
      loaded,
      authenticated,
      accepting,
      reviews = [],
      nextReviews = false,
      reviewsCount = 0,
      averageRating = 0,
      atmosphereReviews = {},
      isApplicable = false,
      title,
      business: { id: businessId, name, displayName } = {},
      businessFeatures = [],
    } = this.state;

    if (!loaded) return;
    const isBusinessKyotango = businessFeatures.includes(KYOTANGO);

    return (
      <>
        {businessFeatures.includes(ANA) && (
          <AnaAlert
            classes={{ root: clsx(classes.container, classes.alert) }}
          />
        )}
        {reviews.length > 0 && (
          <ReviewContainer
            businessId={businessId}
            reviews={reviews}
            count={reviewsCount}
            averageRating={averageRating}
            next={nextReviews}
            atmosphereReviews={atmosphereReviews}
            titleProps={{ id: 'planReviewsContainer' }}
          />
        )}
        {accepting && (!authenticated || isApplicable) && (
          <Application
            authenticated={authenticated}
            title={title}
            planId={this.getPlanId()}
            businessName={displayName || name}
            businessFeatures={businessFeatures}
          />
        )}
      </>
    );
  }

  render() {
    const { authenticated, notificationsCount, room, imageUrl, message } =
      this.props;
    return (
      <AppLayout
        authenticated={authenticated}
        notificationsCount={notificationsCount}
        room={room}
        imageUrl={imageUrl}
        message={message}
      >
        {super.render()}
      </AppLayout>
    );
  }
}

const ShowContainer = (props) => (
  <AuthDialogContextProvider isLoggedIn={props.authenticated}>
    <Show {...props} />
  </AuthDialogContextProvider>
);

export default ShowContainer;
